import { useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { api } from './api';

interface ServiceRequest {
  id: string;
  ticketNumber: string;
  description: string;
  status: string;
  location: string;
  branchId: string;
  createdAt: string;
}

interface UseRealtimeOptions {
  branchId?: string | null;
  onNewRequest?: (request: ServiceRequest) => void;
  onStatusChange?: (request: ServiceRequest, oldStatus: string, newStatus: string) => void;
  onUpdate?: (branchId?: string | null) => void;
  onInitialLoad?: (requests: ServiceRequest[]) => void;
}

export function useRealtimeRequests({
  branchId,
  onNewRequest,
  onStatusChange,
  onUpdate,
  onInitialLoad
}: UseRealtimeOptions) {
  const subscriptionRef = useRef<any>(null);
  const previousRequestStatuses = useRef<Map<string, string>>(new Map());
  const isInitialLoadRef = useRef(true);
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (!branchId) {
      console.log('[Realtime] No branch selected, skipping subscription');
      return;
    }

    let isStale = false;

    const setupSubscription = async () => {
      try {
        // First, fetch current requests to establish baseline
        console.log('[Realtime] Fetching initial requests for branch:', branchId);
        const initialData = await api.get<{ requests: ServiceRequest[] }>(
          `/requests?branchId=${branchId}`
        );

        if (isStale || !isMountedRef.current) {
          console.log('[Realtime] Initial load stale, cancelling subscription');
          return;
        }

        console.log('[Realtime] Initial load: got', initialData.requests.length, 'requests');

        // Store initial statuses
        initialData.requests.forEach((req) => {
          previousRequestStatuses.current.set(req.id, req.status);
        });

        onInitialLoad?.(initialData.requests);
        isInitialLoadRef.current = false;

        // Subscribe to realtime changes for this branch
        console.log('[Realtime] Setting up subscription for branch:', branchId);

        const subscription = supabase
          .channel(`requests:${branchId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'service_request',
              filter: `branchId=eq.${branchId}`,
            },
            (payload: any) => {
              if (!isMountedRef.current) {
                console.log('[Realtime] Component unmounted, ignoring event');
                return;
              }

              console.log('[Realtime] Event received:', payload.eventType, payload.new?.id);

              const newRequest = payload.new as ServiceRequest | undefined;
              const oldRequest = payload.old as ServiceRequest | undefined;

              if (payload.eventType === 'INSERT' && newRequest) {
                console.log('[Realtime] New request created:', newRequest.ticketNumber);
                previousRequestStatuses.current.set(newRequest.id, newRequest.status);
                onNewRequest?.(newRequest);
                onUpdate?.(branchId);
              } else if (payload.eventType === 'UPDATE' && newRequest) {
                const oldStatus = previousRequestStatuses.current.get(newRequest.id);
                if (oldStatus && oldStatus !== newRequest.status) {
                  console.log('[Realtime] Status changed:', newRequest.ticketNumber, `${oldStatus} → ${newRequest.status}`);
                  onStatusChange?.(newRequest, oldStatus, newRequest.status);
                  onUpdate?.(branchId);
                }
                previousRequestStatuses.current.set(newRequest.id, newRequest.status);
              } else if (payload.eventType === 'DELETE' && oldRequest) {
                console.log('[Realtime] Request deleted:', oldRequest.ticketNumber);
                previousRequestStatuses.current.delete(oldRequest.id);
                onUpdate?.(branchId);
              }
            }
          )
          .subscribe((status: string) => {
            console.log('[Realtime] Subscription status:', status);
            if (status === 'SUBSCRIBED') {
              console.log('[Realtime] Successfully subscribed to branch:', branchId);
            } else if (status === 'CHANNEL_ERROR') {
              console.error('[Realtime] Channel error for branch:', branchId);
            }
          });

        subscriptionRef.current = subscription;
      } catch (error) {
        console.error('[Realtime] Setup failed:', error);
        onUpdate?.(branchId);
      }
    };

    setupSubscription();

    // Cleanup
    return () => {
      isStale = true;
      isMountedRef.current = false;

      if (subscriptionRef.current) {
        console.log('[Realtime] Cleaning up subscription for branch:', branchId);
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    };
  }, [branchId, onNewRequest, onStatusChange, onUpdate, onInitialLoad]);
}
